import React, { useCallback, useEffect, useRef, useState } from "react";
import Mark, { MarkOptions } from "mark.js";

import { ClassName, OFFSET_TOP } from "./settings";
import { RecordTextWithMarks } from "./RecordTextWithMarks";
import { FindOnPage } from "./FindOnPage";

interface IProps {
  text: string;
}

export const HighlightFind: React.FC<IProps> = ({ text }) => {
  const markInstance = useRef<Mark | null>(null);
  const textRef = useRef<HTMLElement>(null);
  const scrollbar = useRef<Element | null>(null);
  const [currentIndex, setCurrent] = useState(0);
  const [marks, setMarks] = useState<Array<HTMLElement>>([]);
  const resetSearch = useCallback((options: MarkOptions) => {
    const { current } = markInstance;
    setCurrent(0);
    setMarks([]);
    if (current) {
      current.unmark(options);
    }
  }, []);
  const doneSearch = useCallback(() => {
    const { current } = textRef;
    if (current) {
      setMarks([].slice.call(current.querySelectorAll(`mark.${ClassName.find}`)));
    }
  }, []);
  const searching = useCallback((value: string, options: MarkOptions) => {
    const { current } = markInstance;

    if (current) {
      current.mark(value, options);
    }
  }, []);
  /* initialize mark and scrollbar refs */
  useEffect(() => {
    const { current } = textRef;

    if (current) {
      markInstance.current = new Mark(current);
      markInstance.current.unmark();
      scrollbar.current = current.closest("div.ScrollbarsCustom-Scroller");
    }
  }, []);
  /* remove all marks */
  useEffect(() => {
    const { current } = markInstance;

    if (current) {
      current.unmark();
    }
  }, [text]);
  /* toggle class .current */
  useEffect(() => {
    const current = marks[currentIndex];
    const siblings = [
      marks[currentIndex - 1],
      marks[currentIndex + 1],
    ];

    if (current) {
      const { offsetTop, classList } = current;
      classList.add("current");

      if (scrollbar.current) {
        scrollbar.current.scrollTo(0, offsetTop - OFFSET_TOP);
      }
    }

    siblings.forEach((sibling) => {
      if (sibling) {
        sibling.classList.remove("current");
      }
    });
  }, [marks, currentIndex]);

  return (
    <>
      <FindOnPage
        currentIndex={currentIndex}
        setCurrent={setCurrent}
        markLength={marks.length}
        resetSearch={resetSearch}
        doneSearch={doneSearch}
        searching={searching}
      />

      <RecordTextWithMarks
        ref={textRef}
        css="position: absolute; top: 0; right: 0; bottom: 0; left: 0;"
      >
        {text}
      </RecordTextWithMarks>
    </>
  );
};
